import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { api } from '../../../core/network/api';
import { IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { useGeneratePdf } from '../../reports/contexts/reports';
import { downloadPdf } from '../../reports/utils/shell';
import { transferPaths } from '../queries/paths';
import { pregnancyPaths } from '../../pregnancies/queries/paths';
import { buildOpuTransferReport } from '../utils/report';

// Modal de generación del informe PDF de T.E. (por OPU). El usuario filtra qué
// sesiones incluir: una finca / todas las fincas de un cliente / todas. Las fincas
// y clientes salen de las propias sesiones de la OPU (endpoint derivado) — no hace
// falta catálogo. Al generar: por cada sesión se trae el ensamblado (linaje +
// remarks CL/OBS) y, de una preñez, el bloque `forecast` (dx1/dx2/parto estimados).
export default function TransferReportModal({ opuCode, sessions, onClose }) {
    const { mutateAsync: generatePdf } = useGeneratePdf();
    const cardRef = useModalDialog(onClose);
    const [scope, setScope] = useState({ type: 'all' });
    const [busy, setBusy] = useState(false);

    const list = useMemo(() => (sessions || []).filter((s) => s.status !== 0), [sessions]);

    // árbol cliente → fincas, derivado de las sesiones de la OPU.
    const clients = useMemo(() => {
        const byClient = new Map();
        list.forEach((s) => {
            const cid = s.client?.id ?? 'none';
            if (!byClient.has(cid)) byClient.set(cid, { id: cid, name: s.client?.name || 'Sin cliente', farms: new Map() });
            const c = byClient.get(cid);
            if (s.farm && !c.farms.has(s.farm.id)) c.farms.set(s.farm.id, s.farm);
        });
        return [...byClient.values()].map((c) => ({ ...c, farms: [...c.farms.values()] }));
    }, [list]);

    const matches = (s) => {
        if (scope.type === 'all') return true;
        if (scope.type === 'client') return (s.client?.id ?? 'none') === scope.id;
        if (scope.type === 'farm') return s.farm?.id === scope.id;
        return false;
    };
    const selected = list.filter(matches);
    const selectedTransfers = selected.reduce((a, s) => a + (s.transfers?.length || 0), 0);

    const scopeLabel = () => {
        if (scope.type === 'all') return 'Todas las fincas';
        if (scope.type === 'client') return clients.find((c) => c.id === scope.id)?.name || 'Cliente';
        if (scope.type === 'farm') {
            for (const c of clients) { const f = c.farms.find((x) => x.id === scope.id); if (f) return f.name; }
        }
        return '';
    };

    const isSel = (s) => (s.type === scope.type) && (s.type === 'all' || s.id === scope.id);

    const generate = async () => {
        if (!selected.length) { toast.error('No hay sesiones T.E. para el filtro elegido'); return; }
        setBusy(true);
        const failures = [];
        try {
            const items = [];
            for (const s of selected) {
                let full;
                try { full = await api.get(transferPaths.session(s.id)).then((r) => r.data); }
                catch { failures.push(s.code || `#${s.id}`); continue; }

                const transfers = full.transfers || [];
                // fechas globales de la sesión (mismas para todas: misma fecha TE):
                // el bloque `forecast` de la preñez las da derivadas de la fecha TE
                // (0026 §3) aunque no existan filas de dx. parto = pregnancy.estimated
                // como respaldo si no hay acceso a la preñez.
                let parto = transfers.find((t) => t.pregnancy?.estimated)?.pregnancy?.estimated || null;
                let dx1 = null;
                let dx2 = null;
                const pregId = transfers.find((t) => t.pregnancy?.id)?.pregnancy?.id;
                if (pregId) {
                    try {
                        const preg = await api.get(pregnancyPaths.detail(pregId)).then((r) => r.data);
                        const f = preg.forecast || {};
                        dx1 = f.dx1_estimated || null;
                        dx2 = f.dx2_estimated || null;
                        parto = f.parto_estimated || preg.estimated || parto;
                    } catch { /* sin pregnancies:read o sin preñez → dx queda en "—" */ }
                }
                items.push({ session: full, dates: { dx1, dx2, parto } });
            }

            if (!items.length) { toast.error(`No se pudo generar: sin acceso a las sesiones (${failures.join(', ')})`); return; }

            const { html, filename, options } = buildOpuTransferReport(items, { opuCode, scopeLabel: scopeLabel() });
            const blob = await generatePdf({ html, filename, options });
            downloadPdf(blob, filename);
            if (failures.length) toast(`Informe generado (omitidas sin acceso: ${failures.join(', ')})`);
            else toast.success('Informe generado');
            onClose();
        } catch (err) {
            if (err?.status !== 403) toast.error(err?.message || 'No se pudo generar el informe'); // 403 lo avisa el interceptor
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="transfer-modal">
            <div className="transfer-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="transfer-modal__card" role="dialog" aria-modal="true" aria-label="Generar informe de transferencias" ref={cardRef}>
                <header className="transfer-modal__head">
                    <h2 className="transfer-modal__title">Informe de T.E.{opuCode ? ` · OPU ${opuCode}` : ''}</h2>
                    <button type="button" className="transfer-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="transfer-modal__body">
                    {!list.length ? (
                        <p className="transfer-field__hint">Esta OPU no tiene sesiones de transferencia.</p>
                    ) : (
                        <>
                            <span className="transfer-field__label">Incluir en el informe</span>
                            <div className="transfer-report-scope">
                                <label className="transfer-report-scope__opt">
                                    <input type="radio" name="te-report-scope" checked={isSel({ type: 'all' })} onChange={() => setScope({ type: 'all' })} disabled={busy} />
                                    <span>Todas las fincas</span>
                                </label>

                                {clients.map((c) => (
                                    <div key={c.id} className="transfer-report-scope__client">
                                        {c.farms.length > 1 && (
                                            <label className="transfer-report-scope__opt transfer-report-scope__opt--group">
                                                <input type="radio" name="te-report-scope" checked={isSel({ type: 'client', id: c.id })} onChange={() => setScope({ type: 'client', id: c.id })} disabled={busy} />
                                                <span>Todas las fincas de {c.name}</span>
                                            </label>
                                        )}
                                        {c.farms.map((f) => (
                                            <label key={f.id} className="transfer-report-scope__opt transfer-report-scope__opt--farm">
                                                <input type="radio" name="te-report-scope" checked={isSel({ type: 'farm', id: f.id })} onChange={() => setScope({ type: 'farm', id: f.id })} disabled={busy} />
                                                <span>{f.name}<small> · {c.name}</small></span>
                                            </label>
                                        ))}
                                    </div>
                                ))}
                            </div>
                            <span className="transfer-field__hint">{selected.length} sesión(es) · {selectedTransfers} transferencia(s)</span>
                        </>
                    )}
                </div>

                <footer className="transfer-modal__foot">
                    <button type="button" className="transfer-modal__cancel" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button type="button" className="transfer-modal__continue" onClick={generate} disabled={busy || !selected.length}>
                        {busy ? 'Generando…' : 'Generar PDF'}
                    </button>
                </footer>
            </div>
        </div>
    );
}
